import React, { useMemo, useState, useEffect } from 'react';
import { Card, Space, Typography } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getSitesByOrganization, getOrgSiteTypes } from '../../services/siteService';

const { Text } = Typography;

/**
 * MapLegend - Displays site type/status icons and their meanings
 * Shows the same letters and colors used on map markers
 */
const MapLegend = ({ className = '', siteColorMode = 'status', sites = [], siteCounts = null, context = 'default' }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [directSites, setDirectSites] = useState([]);
  const [orgSiteTypes, setOrgSiteTypes] = useState([]);
  
  // Mobile detection with responsive updates
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Inject CSS for mobile two-column layout
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'map-legend-mobile-styles';
    
    // Remove any existing style element
    const existingStyle = document.getElementById('map-legend-mobile-styles');
    if (existingStyle) {
      document.head.removeChild(existingStyle);
    }
    
    styleElement.textContent = `
      @media (max-width: 768px) {
        .badge-container-mobile {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
          box-sizing: border-box !important;
          align-items: flex-start !important;
          justify-content: space-between !important;
        }
        .badge-container-mobile > div {
          width: 48% !important;
          box-sizing: border-box !important;
          min-width: 0 !important;
          flex: 0 0 48% !important;
        }
        .map-legend {
          width: 100% !important;
          max-width: none !important;
          display: block !important;
        }
        /* Only show first legend if multiple exist */
        .map-legend-crisis:nth-of-type(n+1) {
          display: none !important;
        }
        /* Show dashboard legend first, hide crisis legend */
        .map-legend-dashboard ~ .map-legend-crisis {
          display: none !important;
        }
      }
    `;
    
    document.head.appendChild(styleElement);

    return () => {
      const element = document.getElementById('map-legend-mobile-styles');
      if (element) {
        document.head.removeChild(element);
      }
    };
  }, [isMobile]);
  
  // Load sites and site types directly in MapLegend
  useEffect(() => {
    const loadDataDirectly = async () => {
      if (currentUser?.orgId) {
        try {
          // Load sites
          const directSites = await getSitesByOrganization(currentUser.orgId);
          setDirectSites(directSites);
          
          // Load organization site types
          const orgTypes = await getOrgSiteTypes(currentUser.orgId);
          setOrgSiteTypes(orgTypes);
        } catch (error) {
          console.error('MapLegend: Error loading data:', error);
          setDirectSites([]);
          setOrgSiteTypes([]);
        }
      }
    };
    
    loadDataDirectly();
  }, [currentUser?.orgId]);
  
  // Use live sites prop when available, fallback to direct sites only if props are empty
  const workingSites = sites.length > 0 ? sites : directSites;

  // Create dynamic site type legend from organization's registered types
  const SITE_TYPE_LEGEND = useMemo(() => {
    const legend = {};
    
    // Convert org site types to legend format
    orgSiteTypes.forEach(type => {
      legend[type.name] = {
        color: type.color,
        letter: type.letter,
        label: type.display_name
      };
    });
    
    // Fallback for sites with unregistered types (shouldn't happen with auto-registration)
    const fallbackTypes = {
    other: { color: '#95a5a6', letter: 'O', label: 'Other' }
  };
    
    return { ...legend, ...fallbackTypes };
  }, [orgSiteTypes]);

  // Site status colors and mappings (matching MapCore)
  const SITE_STATUS_LEGEND = {
    active: { color: '#27ae60', letter: '✓', label: 'Active' },
    degraded: { color: '#f39c12', letter: '⚠', label: 'Degraded' },
    evacuating: { color: '#e74c3c', letter: '🚨', label: 'Evacuating' },
    evacuated: { color: '#9b59b6', letter: '🚪', label: 'Evacuated' },
    closed_and_secure: { color: '#3498db', letter: '🔒', label: 'Closed and Secure' },
    unknown: { color: '#95a5a6', letter: '?', label: 'Unknown' }
  };

  // Calculate legend counts from working sites
  const legendCounts = useMemo(() => {
    const counts = {};
    
    if (siteColorMode === 'status') {
      workingSites.forEach(site => {
        const status = site.status || 'unknown';
        counts[status] = (counts[status] || 0) + 1;
      });
      
      // Ensure all legend statuses are represented
      Object.keys(SITE_STATUS_LEGEND).forEach(status => {
        if (!(status in counts)) counts[status] = 0;
      });
    } else {
      workingSites.forEach(site => {
        const type = site.site_type || 'other';
        counts[type] = (counts[type] || 0) + 1;
      });
      
      // Ensure all legend types are represented
      Object.keys(SITE_TYPE_LEGEND).forEach(type => {
        if (!(type in counts)) counts[type] = 0;
      });
    }
    
    return counts;
  }, [workingSites, siteColorMode, SITE_STATUS_LEGEND, SITE_TYPE_LEGEND]);

  // Create legend icon SVG
  const createLegendIcon = (item) => {
    const size = 24;
    const center = size / 2;
    const innerRadius = center * 0.5;
    
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <path d="M${center} 2C${center - 6} 2 2 ${center - 6} 2 ${center}c0 ${center - 2} ${size - 2} ${size - 2} ${size - 2} ${size - 2}s${size - 2}-${center + 2} ${size - 2}-${size - 2}C${size - 2} ${center - 6} ${center + 6} 2 ${center} 2z" 
              fill="${item.color}" 
              stroke="${item.color}" 
              stroke-width="1"/>
        <circle cx="${center}" cy="${center}" r="${innerRadius * 0.8}" fill="white"/>
        <text x="${center}" y="${center + 3}" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="${item.color}">
          ${item.letter}
        </text>
      </svg>
    `)}`;
  };

  // Render legend item with count
  const renderLegendItem = (type, item) => {
    const count = legendCounts[type] || 0;
    
    // Mobile: Shorten text labels to fit in two-column layout
    const mobileLabel = isMobile ? item.label.replace(/Closed and Secure/g, 'Closed').replace(/Evacuating/g, 'Evac').replace(/Evacuated/g, 'Evacuated').replace(/Unknown/g, 'Unknown') : item.label;
    
    return (
      <div 
        key={type}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '4px' : '6px',
          padding: isMobile ? '3px 6px' : '4px 8px',
          borderRadius: '4px',
          backgroundColor: '#fafafa',
          border: '1px solid #f0f0f0',
          // Mobile: Full width within each column with vertical spacing
          ...(isMobile ? {
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            minWidth: '0',
            marginBottom: '4px' // Vertical spacing between badges
          } : {
          minWidth: 'fit-content',
            whiteSpace: 'nowrap',
            flex: 'none'
          })
        }}
      >
        <img 
          src={createLegendIcon(item)}
          alt={item.label}
          style={{ 
            width: isMobile ? '18px' : '20px', 
            height: isMobile ? '18px' : '20px',
            flexShrink: 0 
          }} 
        />
        <span style={{ 
          fontSize: isMobile ? '9px' : '11px', // Smaller font for 2-per-row
            color: '#666',
          fontWeight: 500,
          whiteSpace: isMobile ? 'nowrap' : 'normal',
          overflow: isMobile ? 'hidden' : 'visible',
          textOverflow: isMobile ? 'ellipsis' : 'initial',
          lineHeight: isMobile ? '1.2' : 'normal' // Tighter line height on mobile
        }}>
          {mobileLabel} ({count})
        </span>
      </div>
    );
  };

  return (
    <Card 
      className={`map-legend map-legend-${context} ${className}`}
      size="small"
      style={{ 
        marginTop: '8px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}
      styles={{ 
        body: { 
          padding: '8px',
          // Mobile: Allow flexbox layout
          ...(isMobile && {
            width: '100%',
            minWidth: '0',
            overflow: 'visible'
          })
        } 
      }}
    >
      
        
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '4px',
        marginBottom: '8px'
      }}>
        <Text 
          strong 
          style={{ 
            fontSize: '13px', 
            color: '#39325b',
            marginRight: '8px'
          }}
        >
          {siteColorMode === 'status' ? 'Site Status:' : 'Site Types:'}
        </Text>
      </div>
      
        {/* Regular badge container */}
            <div 
              style={{
                position: 'relative',
                width: '100%',
                height: 'auto',
                minHeight: isMobile ? '170px' : '55px', // Mobile: Extended by 50px (was 120px), Desktop: reduced by 145px total (was 200px)
                padding: '8px',
                boxSizing: 'border-box'
              }}
            >
              {/* 2-column layout for mobile */}
              {isMobile ? (
                <>
                  {/* LEFT COLUMN - ABSOLUTE POSITIONED */}
                  <div style={{ 
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    width: 'calc(50% - 12px)',
                    minHeight: '150px', // Extended by 50px for mobile (was 100px)
                    boxSizing: 'border-box',
                    padding: '4px'
                  }}>
                    {Object.entries(siteColorMode === 'status' ? SITE_STATUS_LEGEND : SITE_TYPE_LEGEND)
                      .slice(0, 3)
                      .map(([key, item]) => renderLegendItem(key, item))
                    }
                  </div>
                  
                  {/* RIGHT COLUMN - ABSOLUTE POSITIONED */}
                  <div style={{ 
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: 'calc(50% - 12px)',
                    minHeight: '150px', // Extended by 50px for mobile (was 100px)
                    boxSizing: 'border-box',
                    padding: '4px'
                  }}>
                    {Object.entries(siteColorMode === 'status' ? SITE_STATUS_LEGEND : SITE_TYPE_LEGEND)
                      .slice(3, 6)
                      .map(([key, item]) => renderLegendItem(key, item))
                    }
                  </div>
                </>
              ) : (
                // 🖥️ DESKTOP: Single row layout with flex wrap
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center'
      }}>
        {Object.entries(siteColorMode === 'status' ? SITE_STATUS_LEGEND : SITE_TYPE_LEGEND).map(([key, item]) => 
          renderLegendItem(key, item)
                  )}
                </div>
        )}
      </div>
      
      <div style={{ 
        marginTop: '8px', 
        fontSize: '11px', 
        color: '#999',
        textAlign: 'center'
      }}>
        {siteColorMode === 'status' 
          ? 'Colors indicate current operational status • Letters show site types'
          : 'Colors and letters indicate site types • Click any marker for details'
        }
      </div>
    </Card>
  );
};

export default MapLegend; 